// Driftslogg Sandbox Beta - API Client
// Technical Council of Elders - Mock API Integration

import axios from 'axios';

// API Configuration
const API_BASE_URL = 'http://localhost:3001/api';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add request timestamp for debugging
    config.metadata = { startTime: new Date() };
    
    // Add sandbox mode header
    config.headers['X-Sandbox-Mode'] = 'true';
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Calculate request duration
    const endTime = new Date();
    const duration = endTime - response.config.metadata.startTime;
    
    console.log(`📡 API Request: ${response.config.method?.toUpperCase()} ${response.config.url} - ${duration}ms`);
    
    return response;
  },
  (error) => {
    // Handle offline mode
    if (!navigator.onLine) {
      console.warn('📴 Offline mode - API request failed');
      return Promise.reject(new Error('Application is offline'));
    }
    
    // Handle server errors
    if (error.response?.status >= 500) {
      console.error('🚨 Server error:', error.response?.data?.error || error.message);
    }
    
    return Promise.reject(error);
  }
);

// API Methods
export const api = {
  // System API
  system: {
    health: () => apiClient.get('/health'),
    info: () => apiClient.get('/info'),
  },

  // Logs API
  logs: {
    getAll: (params = {}) => apiClient.get('/logs', { params }),
    getById: (id) => apiClient.get(`/logs/${id}`),
    create: (data) => apiClient.post('/logs', data),
    update: (id, data) => apiClient.put(`/logs/${id}`, data),
    delete: (id) => apiClient.delete(`/logs/${id}`),
  },

  // Checklists API
  checklists: {
    getTemplates: () => apiClient.get('/checklists/templates'),
    createTemplate: (data) => apiClient.post('/checklists/templates', data),
    getSubmissions: (params = {}) => apiClient.get('/checklists/submissions', { params }),
    createSubmission: (data) => apiClient.post('/checklists/submissions', data),
  },

  // RAYVN Integration API
  rayvn: {
    sync: (action, data = {}) => apiClient.post('/rayvn/sync', { action, data }),
    testConnection: () => apiClient.post('/rayvn/sync', { action: 'test_connection' }),
    sendLog: (logData) => apiClient.post('/rayvn/sync', { 
      action: 'send_log_to_rayvn', 
      data: logData 
    }),
    syncIncidents: () => apiClient.post('/rayvn/sync', { action: 'sync_incidents' }),
  },

  // File Upload API
  upload: {
    file: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiClient.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
  },

  // Demo Utilities
  demo: {
    simulateRayvnEvent: (eventType, data = {}) => 
      apiClient.post('/demo/simulate-rayvn-event', { event_type: eventType, data }),
  },
};

// Utility functions
export const apiUtils = {
  // Handle API errors consistently
  handleError: (error) => {
    if (error.response?.data?.error) {
      return error.response.data.error;
    }
    if (error.message) {
      return error.message;
    }
    return 'An unexpected error occurred';
  },

  // Format API response data
  formatResponse: (response) => {
    return response.data?.data || response.data;
  },

  // Check if API is available
  isApiAvailable: async () => {
    try {
      await api.system.health();
      return true;
    } catch (error) {
      return false;
    }
  },

  // Get API base URL
  getApiBaseUrl: () => API_BASE_URL,

  // Offline storage utilities
  offlineStorage: {
    save: (key, data) => {
      try {
        localStorage.setItem(`driftslogg_offline_${key}`, JSON.stringify({
          data,
          timestamp: new Date().toISOString(),
          synced: false
        }));
      } catch (error) {
        console.error('Failed to save offline data:', error);
      }
    },

    load: (key) => {
      try {
        const stored = localStorage.getItem(`driftslogg_offline_${key}`);
        return stored ? JSON.parse(stored) : null;
      } catch (error) {
        console.error('Failed to load offline data:', error);
        return null;
      }
    },

    remove: (key) => {
      try {
        localStorage.removeItem(`driftslogg_offline_${key}`);
      } catch (error) {
        console.error('Failed to remove offline data:', error);
      }
    },

    getUnsyncedItems: () => {
      const items = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('driftslogg_offline_')) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            if (!data.synced) {
              items.push({ key, ...data });
            }
          } catch (error) {
            console.error('Failed to parse offline item:', error);
          }
        }
      }
      return items;
    }
  }
};

// Export default client for direct use
export default apiClient;